'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchClipStats,
  fetchMyReactions,
  fetchPostHogClipViews,
  setClipReaction,
} from '@/lib/clip-engagement-api';
import { EMPTY_STATS, toggleReaction, type ClipStats } from '@/lib/clip-engagement';

type Counts = Omit<ClipStats, 'clip_id'>;

/**
 * Views / hearts / comment counts for a list of clips, plus the signed-in
 * user's hearts. `available` stays false when the engagement schema isn't
 * deployed or the read failed: callers render nothing in that case.
 */
export function useClipEngagement(clipIds: string[], userId?: string | null) {
  const key = clipIds.join(',');
  const ids = useMemo(() => (key ? key.split(',') : []), [key]);
  const [available, setAvailable] = useState(false);
  const [stats, setStats] = useState<Record<string, Counts>>({});
  const [reacted, setReacted] = useState<Set<string>>(new Set());

  // Last state the server confirmed, per clip, and the state the user wants.
  const serverRef = useRef<Map<string, boolean>>(new Map());
  const desiredRef = useRef<Map<string, boolean>>(new Map());
  const inflightRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    if (ids.length === 0) return;
    fetchClipStats(ids).then((res) => {
      if (cancelled) return;
      setAvailable(res.available);
      if (res.available) setStats(res.stats);
    });
    return () => {
      cancelled = true;
    };
  }, [ids]);

  useEffect(() => {
    let cancelled = false;
    if (!userId || !available || ids.length === 0) {
      setReacted(new Set());
      return;
    }
    fetchMyReactions(ids, userId).then((set) => {
      if (cancelled) return;
      serverRef.current = new Map(ids.map((id) => [id, set.has(id)]));
      setReacted(set);
    });
    return () => {
      cancelled = true;
    };
  }, [ids, userId, available]);

  // Lifetime views from PostHog (app + web, signed in or not). The Supabase
  // counter only covers signed-in web views since Oct 2026, so show the higher.
  const [postHogViews, setPostHogViews] = useState<Record<string, number>>({});
  useEffect(() => {
    let cancelled = false;
    if (ids.length === 0) return;
    fetchPostHogClipViews(ids).then((views) => {
      if (!cancelled) setPostHogViews(views);
    });
    return () => {
      cancelled = true;
    };
  }, [ids]);

  const countsFor = useCallback(
    (clipId: string): Counts => {
      const base = stats[clipId] ?? EMPTY_STATS;
      const ph = postHogViews[clipId] ?? 0;
      return ph > base.view_count ? { ...base, view_count: ph } : base;
    },
    [stats, postHogViews],
  );

  const adjust = useCallback((clipId: string, field: keyof Counts, delta: number) => {
    if (field === 'view_count') {
      setPostHogViews((prev) =>
        prev[clipId] ? { ...prev, [clipId]: Math.max(0, prev[clipId] + delta) } : prev,
      );
    }
    setStats((prev) => {
      const cur = prev[clipId] ?? EMPTY_STATS;
      return { ...prev, [clipId]: { ...cur, [field]: Math.max(0, cur[field] + delta) } };
    });
  }, []);

  const applyLocal = useCallback(
    (clipId: string, on: boolean) => {
      setReacted((prev) => {
        if (prev.has(clipId) === on) return prev;
        const next = new Set(prev);
        if (on) next.add(clipId);
        else next.delete(clipId);
        return next;
      });
    },
    [],
  );

  /** Optimistic heart toggle; rapid taps collapse into the final state. */
  const toggle = useCallback(
    (clipId: string): boolean | null => {
      if (!userId || !available) return null;
      const current = desiredRef.current.get(clipId) ?? reacted.has(clipId);
      const next = toggleReaction({ reacted: current, count: 0 }).reacted;
      desiredRef.current.set(clipId, next);
      applyLocal(clipId, next);
      adjust(clipId, 'reaction_count', next ? 1 : -1);

      if (!inflightRef.current.has(clipId)) {
        inflightRef.current.add(clipId);
        void (async () => {
          try {
            for (;;) {
              const want = desiredRef.current.get(clipId)!;
              const have = serverRef.current.get(clipId) ?? false;
              if (want === have) break;
              const ok = await setClipReaction(clipId, userId, want);
              if (!ok) {
                // Roll back to the last confirmed state.
                const latest = desiredRef.current.get(clipId)!;
                desiredRef.current.set(clipId, have);
                applyLocal(clipId, have);
                if (latest !== have) adjust(clipId, 'reaction_count', have ? 1 : -1);
                break;
              }
              serverRef.current.set(clipId, want);
            }
          } finally {
            inflightRef.current.delete(clipId);
          }
        })();
      }
      return next;
    },
    [userId, available, reacted, applyLocal, adjust],
  );

  return { available, countsFor, reacted, toggle, adjust };
}
